import React, { useState } from "react";

import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import useTabId from "hooks/dashboard/useTabId";
import { Button } from "primereact/button";
import { Column, ColumnBodyOptions, ColumnEditorOptions } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import useDashboardStore from "store/dashboard-store";
import { formatDisplayDatetime } from "utils/time-utils";
import { Annotation, DatasetSpecification } from "../interfaces";
import { DatasetLineChartAnnotationsProps } from "./DatasetLineChartTypes";
import { useTimezone } from "hooks/useTimezone";
import { DATE_FORMATTER_MINUTES } from "utils/constants";

const DatasetLineChartAnnotations = (props: DatasetLineChartAnnotationsProps) => {
  const tz = useTimezone();

  const [currentAnnotations, setCurrentAnnotations] = useState<Annotation[]>(props.annotations ?? []);

  const updateComponent = useDashboardStore(state => state.updateComponent);
  const dashboard = useDashboardStore(state => state.dashboard);
  const tabId = useTabId();

  const { componentId, groupId } = props.store.getState();

  const { data } = useGetRecentData(
    props.datasets.map((dataset: DatasetSpecification) => dataset.identifier),
    {}
  );

  const datasetOptions = data?.map(dataset => ({
    label: dataset.title,
    value: dataset.identifier,
  }));

  const handleRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const { newData, index } = e;
    const updatedAnnotations = [...currentAnnotations];
    updatedAnnotations[index] = newData as Annotation;
    setCurrentAnnotations(updatedAnnotations);
  };

  const handleUpdateLineChartConfigWithAnnotations = () => {
    if (dashboard && dashboard.dashboardTabsJs) {
      const currentTab = dashboard.dashboardTabsJs[tabId];

      if (!currentTab) {
        return;
      }

      const componentGroup = currentTab.componentGroups.find(group => {
        return group.id === groupId;
      });

      const updatedComponent = componentGroup?.components.find(component => component.id === componentId);

      if (updatedComponent) {
        updatedComponent.config = {
          ...updatedComponent.config,
          annotations: currentAnnotations,
        };
        updateComponent(groupId, componentId, updatedComponent, tabId);
        useDashboardStore.getState().triggerTabRefresh();

        props.setIsDialogVisible(false);
      }
    }
  };

  const addAnnotationRow = () => {
    setCurrentAnnotations([
      ...currentAnnotations,
      {
        date: new Date(),
        text: "New Annotation",
        dataset: data ? data[0].identifier : null,
        y: null,
      },
    ]);
  };

  const confirmOnDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, options: ColumnBodyOptions) => {
    const updatedData = [...currentAnnotations];
    updatedData.splice(options.rowIndex, 1);
    confirmPopup({
      target: e.currentTarget,
      message: "Are you sure you want to delete this annotation?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        setCurrentAnnotations(updatedData);
      },
    });
  };

  const handleRowDeleteButton = (options: ColumnBodyOptions) => {
    return <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={e => confirmOnDelete(e, options)} />;
  };

  const annotationTextEditor = (options: ColumnEditorOptions) => {
    return <InputText value={options.value || ""} onChange={e => options.editorCallback?.(e.target.value)} />;
  };

  const annotationDateEditor = (options: ColumnEditorOptions) => {
    return <CalendarWrapper value={new Date(options.value)} onChange={date => options.editorCallback?.(date)} />;
  };

  const annotationDatasetEditor = (options: ColumnEditorOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={datasetOptions}
        onChange={e => options.editorCallback?.(e.value)}
        placeholder="Select a Dataset"
      />
    );
  };

  const annotationsEditorFooter = () => {
    return (
      <div>
        <Button label="Close" onClick={() => props.setIsDialogVisible(false)} />
        <Button label="Save" onClick={handleUpdateLineChartConfigWithAnnotations} className="p-button-success" />
      </div>
    );
  };

  return (
    <Dialog
      header="Manage Annotations"
      visible={props.isDialogVisible}
      onHide={() => {
        props.setIsDialogVisible(false);
      }}
      className="w-full md:w-6"
      footer={annotationsEditorFooter}
    >
      <div>
        <DataTable value={currentAnnotations} paginator rows={5} editMode="row" onRowEditComplete={handleRowEditComplete}>
          <Column
            header="Dataset"
            field="dataset"
            body={rowData => datasetOptions?.find(option => option.value === rowData.dataset)?.label || ""}
            editor={annotationDatasetEditor}
            style={{ width: "40%" }}
          />
          <Column
            header="Date"
            field="date"
            body={rowData => formatDisplayDatetime(rowData.date, tz.display, DATE_FORMATTER_MINUTES)}
            editor={options => annotationDateEditor(options)}
            style={{ width: "15%" }}
          />
          <Column
            header="Annotation"
            field="text"
            body={rowData => rowData.text}
            editor={options => annotationTextEditor(options)}
            style={{ width: "30%" }}
          />
          <Column rowEditor align="center" style={{ width: "10%" }} />
          <Column body={(rowData, options) => handleRowDeleteButton(options)} style={{ width: "5%" }} />
        </DataTable>
        <Button onClick={addAnnotationRow} label="Add Annotation" icon="pi pi-plus" />
      </div>
    </Dialog>
  );
};

export default DatasetLineChartAnnotations;
